/**
 * 작성자 : 홍선영
 * 날짜 : 2024.12.23
 * 기능 : (사내)사내관리-사원정보-사원정보 페이지 사원 목록
 */

import { t } from 'i18next';
import { useRecoilValue } from 'recoil';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { PulseLoader } from 'react-spinners';

import { SearchOptions } from '../../../assets/styles/SearchOptions';
import SelectBox from '../../../components/SelectBox';
import { INIT_WORKER_STATUS, LIST_COUNT, PMS_NORMAL_SUB_CODE, WORK_STATUS_SEARCH2 } from '../../../_constants';
import { BtnGhost } from '../../../components/Button';
import { IComCdList, ISelectBox, ICuser } from 'customTypes';
import useSetListCount from '../../../utils/useSetListCount';
import { setComCdListState } from '../../../utils/setComCdListState';
import { IUser, userState } from '../../../atoms';
import IssueGuide from '../../../components/IssueGuide';
import { LoadingModalBackground } from '../../../assets/styles/Modal';
import 'dayjs/locale/ko'; // 한국어 로케일 추가
import { apiGet } from '../../../services/_common';
import Input from '../../../components/Input';
import { useDebounce } from '../../../utils/useDebounce';
import { formatDateYMD } from '../../../utils/formatDate';
import { getAnnualnum } from '../../../utils/getAnnualnum';
import useTuiGridSize from '../../../hooks/useTuiGridSize';
import i18n from '../../../translation/i18n';
import TuiGridWrapper from '../../../components/Table/TuiGridWrapper';
import TuiGrid from '../../../components/Table/TuiGrid';
import { useOutletContext } from 'react-router-dom';
import Portal from '../../../components/Portal';
import SearchFilterModal from '../../../components/Modal/SearchFilterModal';
import { isMobile } from '../../../utils/checkMobile';

interface IList {
  data: ICuser[] | undefined;
  isLoading: boolean;
  isError: boolean;
  tableState: ICuser[] | null;
  setTableState: Dispatch<SetStateAction<ICuser[] | null>>;
  setRowState: Dispatch<SetStateAction<ICuser | null>>;
}

const List = ({ data, isLoading, isError, tableState, setTableState, setRowState }: IList) => {
  const userInfo = useRecoilValue<IUser>(userState);
  const size = useOutletContext<any>();
  const [rowKey, setRowKey] = useState('');
  const [listCountComCdList, setListCountComCdList] = useState<IComCdList[]>([]); // 검색줄수 공통코드
  const { listCount, setListCount, patchUserMenuAPI } = useSetListCount(); // 검색줄수설정 훅
  const [wWorkstatus, setWWorkstatus] = useState(INIT_WORKER_STATUS);
  const [partList, setPartList] = useState<ISelectBox[]>([]); // 부서명 메뉴 리스트
  const [positionList, setPositionList] = useState<ISelectBox[]>([]); // 직급명 메뉴 리스트
  const [partCd, setPartCd] = useState<ISelectBox>({ type: 'PART_CD', PART_CD: 'A', cdName: t('전체') });
  const [positionCd, setPositionCd] = useState<ISelectBox>({ type: 'POSITION_CD', POSITION_CD: 'A', cdName: t('전체') });
  const [searchOption, setSearchOption] = useState({ userName: '' });
  const wNameRef = useRef<HTMLInputElement>(null);
  const [initTableState, setInitTableState] = useState<ICuser[] | null>(data || null);
  const debouncedUserName = useDebounce(searchOption.userName);
  const [columns, setColumns] = useState<any[]>([]);
  const componentRef = useRef<HTMLDivElement>(null);
  const { tuiHeight } = useTuiGridSize(componentRef);
  const [openModal, setOpenModal] = useState({ status: false, type: '' });

  useEffect(() => {
    if (isMobile()) {
      const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
      const containerPadding = 0.75 * 2 * rootFontSize;
      const colWidth = Math.floor((document.body.clientWidth - containerPadding) / 4);
      setColumns([
        { header: t('사원명'), name: 'userName', sortable: true, width: colWidth },
        { header: t('부서명'), name: 'partName', sortable: true, width: colWidth },
        { header: t('근로 상태'), name: 'uWorkstatusName', align: 'center', sortable: true, width: colWidth },
        { header: t('입사일자'), name: 'uJobdate', align: 'center', sortable: true, width: colWidth },
      ]);
    } else {
      setColumns([
        { header: t('사원명'), name: 'userName', sortable: true, width: 120 },
        { header: t('부서명'), name: 'partName', sortable: true, filter: 'select', minWidth: 120 },
        { header: t('직급'), name: 'positionName', sortable: true, filter: 'select', minWidth: 120 },
        { header: t('근로 상태'), name: 'uWorkstatusName', align: 'center', sortable: true, filter: 'select', minWdth: 120 },
        { header: t('입사일자'), name: 'uJobdate', align: 'center', sortable: true, minWidth: 120 },
      ]);
    }
  }, [i18n.language]);

  const getWorkStatusName = (status: 'I' | 'R' | 'D' | 'T') => {
    return WORK_STATUS_SEARCH2.find((el) => el.wWorkstatus === status)?.cdName || '';
  };

  const formatList = (list: any[]) => {
    const result = list.map((el: any) => ({
      ...el,
      uJobdate: formatDateYMD(el.uJobdate),
      uRetiredate: formatDateYMD(el.uRetiredate),
      uAnnualnum: getAnnualnum(el.uAnnualnum),
      uRannualnum: getAnnualnum(el.uRannualnum),
      uWorkstatusName: getWorkStatusName(el.uWorkstatus),
      uBdate: formatDateYMD(el.uBdate),
    }));
    return result;
  };

  useEffect(() => {
    if (data) {
      const result = formatList(data);
      setInitTableState(result);
    }
  }, [data]);

  useEffect(() => {
    if (initTableState) applyFilter(initTableState);
  }, [debouncedUserName, wWorkstatus.wWorkstatus, partCd.PART_CD, positionCd.POSITION_CD, initTableState]);

  const applyFilter = (array: any[]) => {
    // 검색옵션 변경됐을 때 필터링 처리
    // 필터링 기준
    const filterOptions = {
      userName: searchOption.userName,
    };

    // 필터링된 어레이 리턴, 대소문자구분X
    const filteredArray = array.filter((item: any) => {
      // 근로상태 필터
      const workStatusMatch = wWorkstatus.wWorkstatus === 'A' || item.uWorkstatus === wWorkstatus.wWorkstatus;
      // 부서 필터
      const partMatch = partCd.PART_CD === 'A' || item.partCd === partCd.PART_CD;
      // 직급 필터
      const positionMatch = positionCd.POSITION_CD === 'A' || item.positionCd === positionCd.POSITION_CD;
      // 이름 필터
      const nameMatch = item.userName?.toLowerCase()?.includes(filterOptions.userName?.toLowerCase());

      return workStatusMatch && partMatch && positionMatch && nameMatch;
    });

    if (filteredArray.length > 0) setTableState(filteredArray.map((el: any, i) => ({ ...el, rowKey: i, sortKey: i })));
    else setTableState([]);
  };

  // 부서명, 직급명 조회
  const setPmsNormalSubList = async (subCd: string, setState: Dispatch<SetStateAction<ISelectBox[]>>) => {
    const req = { hCd: userInfo.hCd, sCd: userInfo.sCd, subCd };
    const res = await apiGet({ path: '/code/pmsNormalTitle', req });
    const { status } = res;
    const cdFind: string = PMS_NORMAL_SUB_CODE.find((el) => el.code.includes(subCd))?.type || '';
    if (status === 200) {
      const defaultObject = { type: cdFind, [cdFind]: 'A', cdName: t('전체') };
      const newArr = res.data.data.pmsNormalTitleList?.map((el: ISelectBox) => ({ type: cdFind, [cdFind]: el.subCd, cdName: el.cdName }));
      setState([defaultObject, ...newArr]);
    }
  };

  useEffect(() => {
    setPmsNormalSubList('L', setPartList);
    setPmsNormalSubList('M', setPositionList);
    setComCdListState(LIST_COUNT, setListCountComCdList, false);
    // setPartCd({ type: subCodeFilter('L'), [subCodeFilter('L')]: '' });
    // setPositionCd({ type: subCodeFilter('M'), [subCodeFilter('M')]: '' });
  }, []);

  const onClickInitiateSearchOption = () => {
    setWWorkstatus(INIT_WORKER_STATUS);
    setPartCd({ type: 'PART_CD', PART_CD: 'A', cdName: t('전체') });
    setPositionCd({ type: 'POSITION_CD', POSITION_CD: 'A', cdName: t('전체') });
    setSearchOption({ userName: '' });
    setRowState(null);
    setRowKey('');
  };

  const onClickFilter = () => {
    setOpenModal({ status: true, type: 'filter' });
  };

  const handlFilterSearch = () => {
    setOpenModal({ status: false, type: '' });
  };

  const onClickRow = (row: ICuser, columnName: string, filteredIndex: number) => {
    if (row) {
      setRowState(row);
      setRowKey(`${filteredIndex}`);
    }
  };

  const isListLoading = !data || isLoading;

  if (isError) return <IssueGuide />;
  if (isListLoading) {
    return (
      <LoadingModalBackground>
        <PulseLoader className='flex-center' color='rgb(0, 122, 255)' size='1rem' />
      </LoadingModalBackground>
    );
  }

  return (
    <>
      <SearchOptions>
        {size.innerSize.W > 1024 ? (
          <>
            <div className='inputsWrapper'>
              <div className='inputForm-col withLabelComCf'>
                <label htmlFor='wWorkstatus'>{t('근로 상태')}</label>
                <SelectBox
                  options={WORK_STATUS_SEARCH2}
                  defaultOption={t('재직')}
                  state={wWorkstatus}
                  setState={setWWorkstatus}
                  stateKey='wWorkstatus'
                  initiateKey={wWorkstatus.wWorkstatus}
                  filterbar='filter-1-left'
                />
              </div>
              <div className='inputForm-col withLabelComCf'>
                <label htmlFor='partCd'>{t('부서명')}</label>
                <SelectBox options={partList} defaultOption={t('전체')} state={partCd} setState={setPartCd} stateKey='PART_CD' initiateKey={partCd.PART_CD} filterbar='filter-1-left' />
              </div>
              <div className='inputForm-col withLabelComCf'>
                <label htmlFor='positionCd'>{t('직급')}</label>
                <SelectBox
                  options={positionList}
                  defaultOption={t('전체')}
                  state={positionCd}
                  setState={setPositionCd}
                  stateKey='POSITION_CD'
                  initiateKey={positionCd.POSITION_CD}
                  filterbar='filter-1-left'
                />
              </div>
              <div className='inputForm-row'>
                <div className='inputForm-col'>
                  <Input placeholder={t('사원명')} label='' type='text' id='userName' name='userName' state={searchOption} setState={setSearchOption} inputRef={wNameRef} />
                </div>
              </div>
            </div>
            <div className='inputsWrapper'>
              <div className='secondSearchOption'>
                <div className='flex-basic textBtnGroup'>
                  <BtnGhost onClick={onClickInitiateSearchOption}>{t('초기화')}</BtnGhost>
                  <div className='searchResult'>
                    {t('총')}
                    <span>{tableState?.length ?? 0}</span>
                    {t('개')}
                  </div>
                  <div className='inputForm-row'>
                    <div className='inputForm-col withLabelComCf'>
                      <label htmlFor='useYn'>{t('보기 설정')}</label>
                      <SelectBox
                        options={listCountComCdList}
                        defaultOption={listCount.cdName}
                        state={listCount}
                        setState={setListCount}
                        stateKey={LIST_COUNT}
                        initiateKey={listCount[LIST_COUNT]}
                        setTableLinesAPI={patchUserMenuAPI}
                        optionHeight='height-sm'
                        rsearch
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className='filter-wrapper'>
            <div className='inputsWrapper'>
              <div className='inputForm-row' style={{ width: 'calc(100% - 3.5rem)' }}>
                <div className='inputForm-col'>
                  <Input placeholder={t('사원명')} label='' type='search' id='userName' name='userName' state={searchOption} setState={setSearchOption} inputRef={wNameRef} ico='search' />
                </div>
              </div>
              <BtnGhost onClick={onClickFilter} className='filter-btn'>
                <span className='material-symbols-rounded'>filter_alt</span>
              </BtnGhost>
            </div>
          </div>
        )}
      </SearchOptions>
      {tableState && (
        <TuiGridWrapper componentRef={componentRef}>
          <TuiGrid
            data={tableState}
            columns={columns}
            perPage={Number(listCount[LIST_COUNT])}
            usePagenation
            onClickRow={onClickRow}
            rowKey={rowKey}
            frozenCount={size.innerSize.W > 1024 ? 1 : 0}
            height={tuiHeight}
            idxKey='not use'
          />
        </TuiGridWrapper>
      )}
      {isListLoading && (
        <LoadingModalBackground>
          <PulseLoader className='flex-center' color='rgb(0, 122, 255)' size='1rem' />
        </LoadingModalBackground>
      )}
      {size.innerSize.W <= 1024 && (
        <Portal openModal>
          <SearchFilterModal openModal={openModal} setOpenModal={setOpenModal} onClickSearch={handlFilterSearch} onClickReset={onClickInitiateSearchOption}>
            <div className='filter-option-wrapper'>
              <div className='filter-option-cont'>
                <div className='filter-option-row'>
                  <label htmlFor='wWorkstatus'>{t('근로 상태')}</label>
                  <SelectBox
                    options={WORK_STATUS_SEARCH2}
                    defaultOption={t('재직')}
                    state={wWorkstatus}
                    setState={setWWorkstatus}
                    stateKey='wWorkstatus'
                    initiateKey={wWorkstatus.wWorkstatus}
                    filterbar='filter-1-left'
                  />
                </div>
                <div className='filter-option-row'>
                  <label htmlFor='partCd'>{t('부서명')}</label>
                  <SelectBox options={partList} defaultOption={t('전체')} state={partCd} setState={setPartCd} stateKey='PART_CD' initiateKey={partCd.PART_CD} filterbar='filter-1-left' />
                </div>
                <div className='filter-option-row'>
                  <label htmlFor='positionCd'>{t('직급')}</label>
                  <SelectBox
                    options={positionList}
                    defaultOption={t('전체')}
                    state={positionCd}
                    setState={setPositionCd}
                    stateKey='POSITION_CD'
                    initiateKey={positionCd.POSITION_CD}
                    filterbar='filter-1-left'
                  />
                </div>
              </div>
            </div>
          </SearchFilterModal>
        </Portal>
      )}
    </>
  );
};

export default List;
